import * as CollaboratorsActionTypes from '../../types/collaborators';
import * as CollaboratorsSelectors from '../../selectors/collaborators';
import * as CollaboratorsApis from '../../apis/collaborators';
import * as CollaboratorsErrors from '../../errors/collaborators';
import * as GroupsSelectors from '../../selectors/groups';
import * as GroupsActions from '../groups';
import * as CurrentsSelectors from '../../selectors/current';
import * as CurrentsActions from '../current';
import * as SettingsSelectors from '../../selectors/settings';

/**
 * A collaborator
 * @typedef {object} Collaborator
 * @property {string} id - The collaborator id.
 * @property {number} userId - The collaborator's user id.
 * @property {string} groupId - The collaborator's group id.
 * @property {string} username - The collaborator's username.
 * @property {string} userImage - The collaborator's user image path.
 * @property {string} email - The collaborator's email.
 * @property {string} fullName - The collaborator's full name.
 * @property {number} lastSeen - The collaborator's last seen.
 * @property {boolean} removed - The collaborator was removed from its group.
 * @property {object} permissions - The collaborator permissions.
 * @property {boolean} permissions.invite - The collaborator can invite.
 */

/**
 * Sets collaborators
 * @param {object} payload {<collaboratorId>: <collaboratorData>}
 */
export const setCollaborators = (payload) => ({
    type: CollaboratorsActionTypes.SET_COLLABORATORS,
    payload
});

/**
 * Delete collaborator from store
 * @param {string} id - collaborator id
 */
export const deleteCollaborator = (id) => ({
    type: CollaboratorsActionTypes.DELETE_COLLABORATOR,
    payload: id
});

/**
 * Update a collaborator in store
 * @param {object} payload
 * @param {string} payload.id - The collaborator id.
 * @param {number} [payload.userId] - The collaborator's user id.
 * @param {string} [payload.groupId] - The collaborator's group id.
 * @param {string} [payload.username] - The collaborator's username.
 * @param {string} [payload.userImage] - The collaborator's user image path.
 * @param {string} [payload.email] - The collaborator's email.
 * @param {string} [payload.fullName] - The collaborator's full name.
 * @param {number} [payload.lastSeen] - The collaborator's last seen.
 * @param {boolean} [payload.removed] - The collaborator was removed from its group.
 * @param {object} [payload.permissions] - The collaborator permissions.
 * @param {boolean} [payload.permissions.invite] - The collaborator can invite.
 */
export const editCollaborator = (payload) => ({
    type: CollaboratorsActionTypes.EDIT_COLLABORATOR,
    payload
});

/**
 * Add a new collaborator in store
 * @param {object} payload
 * @param {string} payload.id - The collaborator id.
 * @param {number} [payload.userId] - The collaborator's user id.
 * @param {string} [payload.groupId] - The collaborator's group id.
 * @param {string} [payload.username] - The collaborator's username.
 * @param {string} [payload.userImage] - The collaborator's user image path.
 * @param {string} [payload.email] - The collaborator's email.
 * @param {string} [payload.fullName] - The collaborator's full name.
 * @param {number} [payload.lastSeen] - The collaborator's last seen.
 * @param {boolean} [payload.removed] - The collaborator was removed from its group.
 * @param {object} [payload.permissions] - The collaborator permissions.
 * @param {boolean} [payload.permissions.invite] - The collaborator can invite.
 */
export const addCollaborator = (payload) => ({
    type: CollaboratorsActionTypes.ADD_COLLABORATOR,
    payload
});

/**
 * Creates new collaborators to server and to store
 * @param {array} collaborators - The collaborators to create.
 * @param {number} [collaborators[].userId] - The collaborator's user id.
 * @param {string} [collaborators[].email] - The collaborator's email.
 * @param {string} [collaborators[].fullName] - The collaborator's fullName.
 * @param {string} collaborators[].groupId - The group where to add the collaborator.
 */
export const createCollaborators = (collaborators) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        const contextType = SettingsSelectors.getContextType(getState());
        const contextId = SettingsSelectors.getContextId(getState());

        return {
            contextType,
            contextId,
            collaborators
        };
    }).then(
        CollaboratorsApis.createCollaborators
    ).then((collaborators) =>
        collaborators.map((collaborator) => {
            dispatch(addCollaborator(collaborator));
            return collaborator;
        })
    ).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_ACTIONS_CREATE_COLLABORATORS_UNEXPECTED;
        }

        throw error;
    });

export const createCollaborator =
    (collaborator) => createCollaborators([collaborator]);

/**
 * Update a collaborator in server and in store
 * @param {object} payload
 * @param {string} payload.id - The collaborator id.
 * @param {number} [payload.userId] - The collaborator's user id.
 * @param {string} [payload.groupId] - The collaborator's group id.
 * @param {string} [payload.username] - The collaborator's username.
 * @param {string} [payload.userImage] - The collaborator's user image path.
 * @param {string} [payload.email] - The collaborator's email.
 * @param {string} [payload.fullName] - The collaborator's full name.
 * @param {number} [payload.lastSeen] - The collaborator's last seen.
 * @param {boolean} [payload.removed] - The collaborator was removed from its group.
 * @param {object} [payload.permissions] - The collaborator permissions.
 * @param {boolean} [payload.permissions.invite] - The collaborator can invite.
 */
export const updateCollaborator = (payload) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        const contextType = SettingsSelectors.getContextType(getState());
        const contextId = SettingsSelectors.getContextId(getState());

        return {
            contextType,
            contextId,
            ...payload
        };
    }).then(
        CollaboratorsApis.update
    ).then(() =>
        dispatch(editCollaborator(payload))
    ).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_ACTIONS_UPDATE_COLLABORATOR_UNEXPECTED;
        }

        throw error;
    });

/**
 * Delete collaborator in server and in store
 * @param {string} id - collaborator id
 */
export const removeCollaborator = (id) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        const contextType = SettingsSelectors.getContextType(getState());
        const contextId = SettingsSelectors.getContextId(getState());

        return {
            contextType,
            contextId,
            id
        };
    }).then(
        CollaboratorsApis.remove
    ).then(() =>
        dispatch(deleteCollaborator(id))
    ).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_ACTIONS_CREATE_COLLABORATORS_UNEXPECTED;
        }

        throw error;
    });

/**
 * Invite a collaborator to a collaboration group
 * @param {object} payload
 * @param {string} payload.personalMessage - The personal message to display in the email invitation.
 * @param {string} payload.inviterCollaboratorId - The inviter's collaborator id.
 * @param {string} payload.inviterFullName - The inviter's full name to display in the email invitation.
 * @param {array<string>} payload.emails - The emails to invite.
 */
export const inviteCollaborators = (payload) => (_, getState) =>
    Promise.resolve().then(() => {
        const contextType = SettingsSelectors.getContextType(getState());
        const contextId = SettingsSelectors.getContextId(getState());

        return {
            contextType,
            contextId,
            ...payload
        };
    }).then(
        CollaboratorsApis.inviteCollaborators
    ).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_ACTIONS_INVITE_COLLABORATORS_UNEXPECTED;
        }

        throw error;
    });

/**
 * Invites collaborators
 * @param {object} payload
 * @param {string} payload.filerrIds - The collaboration filerr ids to add in the new group.
 * @param {string} payload.personalMessage - The personal message to display in the email invitation.
 * @param {string} payload.inviterFullName - The inviter's name to display in the email invitation.
 * @param {array<string>} payload.emails - The emails to invite.
 */
export const inviteCollaboratorsToCollaboratorsGroup = (payload) => (dispatch, getState) =>
    Promise.resolve().then(() =>
        GroupsSelectors.getGroupIdByType(getState(), 'collaborators')
    ).then((groupId) => {
        if (groupId) {
            const invitedCollaboratorIds = CollaboratorsSelectors.getCollaboratorIdsByGroupIdAndEmails(
                getState(), groupId, payload.emails
            );

            if (invitedCollaboratorIds.length === payload.emails.length) {
                return dispatch(inviteCollaborators({
                    inviterCollaboratorId: CollaboratorsSelectors.getCurrentCollaboratorIdByGroupType(getState(), 'collaborators'),
                    inviterFullName: payload.inviterFullName,
                    personalMessage: payload.personalMessage,
                    invitedCollaboratorIds
                })).then(() => {
                    dispatch(CurrentsActions.setCurrentGroupId(groupId));
                    dispatch(CurrentsActions.setCurrentCollaboratorId(
                        CollaboratorsSelectors.getCurrentCollaboratorIdByGroupType(getState(), 'collaborators')
                    ));
                });
            } else {
                const collaboratorsToCreate = payload.emails.map((email) => ({
                    email,
                    groupId
                })).concat({
                    groupId,
                    userId: CurrentsSelectors.getCurrentUserId(getState()),
                    fullName: payload.inviterFullName
                });

                return dispatch(createCollaborators(collaboratorsToCreate))
                    .then(() => dispatch(inviteCollaboratorsToCollaboratorsGroup(payload)));
            }
        } else {
            return dispatch(GroupsActions.createGroup('collaborators', payload.filerrIds))
                .then(({ id }) => dispatch(CurrentsActions.setCurrentGroupId(id)))
                .then(() => dispatch(inviteCollaboratorsToCollaboratorsGroup(payload)));
        }
    }).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_ACTIONS_INVITE_COLLABORATORS_TO_COLLABORATORS_GROUP_UNEXPECTED;
        }

        throw error;
    });
